import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { ENV } from '../../config/environment';

export const API_BASE_URL = ENV.API_BASE_URL;

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 15000,
});

// Keys for localStorage
export const TOKEN_KEYS = {
  ACCESS_TOKEN: 'cyzor_access_token',
  REFRESH_TOKEN: 'cyzor_refresh_token',
  USER: 'cyzor_user',
  ORGANIZATION: 'cyzor_organization',
  ROLES: 'cyzor_roles',
  PERMISSIONS: 'cyzor_permissions',
};

const LEGACY_TOKEN_KEYS = {
  ACCESS_TOKEN: 'cyzor_auth_token',
  REFRESH_TOKEN: 'cyzor_refresh_token_legacy',
};

export function getStoredAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEYS.ACCESS_TOKEN) ?? localStorage.getItem(LEGACY_TOKEN_KEYS.ACCESS_TOKEN);
}

export function getStoredRefreshToken(): string | null {
  return localStorage.getItem(TOKEN_KEYS.REFRESH_TOKEN) ?? localStorage.getItem(LEGACY_TOKEN_KEYS.REFRESH_TOKEN);
}

export function persistAuthTokens(accessToken: string, refreshToken?: string): void {
  localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
  localStorage.setItem(LEGACY_TOKEN_KEYS.ACCESS_TOKEN, accessToken);

  if (refreshToken) {
    localStorage.setItem(TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
    localStorage.setItem(LEGACY_TOKEN_KEYS.REFRESH_TOKEN, refreshToken);
  }
}

function isPublicAuthRequest(url?: string): boolean {
  return !!url && /\/api\/auth\/(login|register|refresh)(?:$|\?)/.test(url);
}

export class CyzorApiError extends Error {
  public url: string;
  public method: string;
  public status?: number;
  public apiResponse?: any;
  public isNetworkError: boolean;
  public isCorsError: boolean;
  public detailMessage: string;
  public debugMessage: string;

  constructor(params: {
    url: string;
    method: string;
    status?: number;
    apiResponse?: any;
    isNetworkError?: boolean;
    isCorsError?: boolean;
    message: string;
  }) {
    super(params.message);
    this.name = 'CyzorApiError';
    this.url = params.url;
    this.method = params.method.toUpperCase();
    this.status = params.status;
    this.apiResponse = params.apiResponse;
    this.isNetworkError = !!params.isNetworkError;
    this.isCorsError = !!params.isCorsError;
    this.detailMessage = params.message;
    this.debugMessage = `[Cyzor API Error] ${params.method.toUpperCase()} ${params.url} - Status: ${params.status ?? 'N/A'} - ${params.message}`;
  }
}

// Request Interceptor: Attach Access Token automatically
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const accessToken = getStoredAccessToken();
    if (accessToken && config.headers && !isPublicAuthRequest(config.url)) {
      config.headers.Authorization = `Bearer ${accessToken}`;
      localStorage.setItem(TOKEN_KEYS.ACCESS_TOKEN, accessToken);
      localStorage.setItem('cyzor_auth_token', accessToken);
    } else if (config.headers && isPublicAuthRequest(config.url)) {
      delete config.headers.Authorization;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Response Interceptor: Auto Refresh Token on 401 & Structured Error Wrapping
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean; _proxyRetry?: boolean };

    // Automatic fail-safe CORS proxy retry:
    // If direct request to external URL failed due to browser CORS/Network error (no response),
    // retry via relative route so it proxies through Vite/Node server seamlessly.
    if (!error.response && originalRequest && !originalRequest._proxyRetry) {
      originalRequest._proxyRetry = true;
      if (originalRequest.url && originalRequest.url.startsWith('http')) {
        try {
          const parsed = new URL(originalRequest.url);
          originalRequest.url = parsed.pathname + parsed.search;
        } catch (_) {
          // ignore parsing error
        }
      }
      originalRequest.baseURL = '';
      return apiClient(originalRequest);
    }

    const url = originalRequest?.url || 'unknown-url';
    const method = originalRequest?.method || 'get';
    const status = error.response?.status;
    const apiResponse = error.response?.data;
    const isNetworkError = !error.response || error.code === 'ERR_NETWORK' || error.message === 'Network Error';
    const isCorsError = isNetworkError || error.message.includes('CORS');
    const errorMessage = parseApiErrorMessage(error);

    const cyzorError = new CyzorApiError({
      url,
      method,
      status,
      apiResponse,
      isNetworkError,
      isCorsError,
      message: errorMessage,
    });

    // Check if error is 401 and request was not already retried, and is not a login/register/refresh request
    if (
      status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !url.includes('auth/login') &&
      !url.includes('auth/register') &&
      !url.includes('auth/refresh')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getStoredRefreshToken();

      if (!refreshToken) {
        isRefreshing = false;
        clearAuthData();
        window.dispatchEvent(new CustomEvent('cyzor_unauthorized'));
        return Promise.reject(cyzorError);
      }

      try {
        const refreshUrl = API_BASE_URL.endsWith('/') ? `${API_BASE_URL}api/auth/refresh` : `${API_BASE_URL}/api/auth/refresh`;
        const workspaceId = sessionStorage.getItem('active_workspace_id') || localStorage.getItem('cyzor_active_workspace_id') || undefined;
        const refreshResponse = await axios.post<any>(refreshUrl, { refreshToken, workspaceId });
        const refreshData = refreshResponse.data?.data || refreshResponse.data;

        const newAccessToken = refreshData?.accessToken;
        const newRefreshToken = refreshData?.refreshToken;

        if (newAccessToken) {
          persistAuthTokens(newAccessToken, newRefreshToken);

          apiClient.defaults.headers.common.Authorization = `Bearer ${newAccessToken}`;
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          }

          processQueue(null, newAccessToken);
          isRefreshing = false;
          return apiClient(originalRequest);
        } else {
          throw new Error('Refresh response missing access token');
        }
      } catch (refreshErr) {
        processQueue(refreshErr as AxiosError, null);
        isRefreshing = false;
        clearAuthData();
        window.dispatchEvent(new CustomEvent('cyzor_unauthorized'));
        return Promise.reject(cyzorError);
      }
    }

    return Promise.reject(cyzorError);
  }
);

// Helper to clear localStorage
export function clearAuthData(): void {
  localStorage.removeItem(TOKEN_KEYS.ACCESS_TOKEN);
  localStorage.removeItem(TOKEN_KEYS.REFRESH_TOKEN);
  localStorage.removeItem('cyzor_auth_token');
  localStorage.removeItem('cyzor_refresh_token');
  localStorage.removeItem('cyzor_refresh_token_legacy');
  localStorage.removeItem(TOKEN_KEYS.USER);
  localStorage.removeItem(TOKEN_KEYS.ORGANIZATION);
  localStorage.removeItem(TOKEN_KEYS.ROLES);
  localStorage.removeItem(TOKEN_KEYS.PERMISSIONS);
  localStorage.removeItem('cyzor_active_workspace_id');
  sessionStorage.removeItem('active_workspace_id');
}

// Utility function to extract exact API error messages with friendly fallback translations
export function parseApiErrorMessage(error: unknown): string {
  if (error instanceof CyzorApiError) {
    if (error.detailMessage && !error.detailMessage.startsWith('[Cyzor API Error]')) {
      return error.detailMessage;
    }
  }

  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const responseData = error.response?.data;
    const code = responseData?.error?.code || responseData?.code || responseData?.errorCode;

    // Direct backend error code mapping
    if (code === 'INVALID_CREDENTIALS') {
      return 'E-mail ou senha inválidos. Por favor, verifique suas credenciais.';
    }
    if (code === 'ACCOUNT_DELETED') {
      return 'Esta conta foi desativada ou excluída.';
    }
    if (code === 'ACCOUNT_INACTIVE') {
      return 'Esta conta está inativa. Entre em contato com o suporte ou administrador.';
    }
    if (code === 'NO_ORGANIZATION') {
      return 'Nenhuma organização ativa vinculada a esta conta.';
    }
    if (code === 'EMAIL_ALREADY_EXISTS') {
      return 'Este e-mail corporativo já está cadastrado no sistema.';
    }
    if (code === 'INSUFFICIENT_PERMISSIONS') {
      return 'Você não possui permissão para realizar esta ação.';
    }

    let message = '';

    if (responseData) {
      if (typeof responseData === 'string') {
        message = responseData.replace(/<[^>]*>?/gm, '').trim();
      } else if (typeof responseData === 'object') {
        if (typeof responseData.error === 'object' && typeof responseData.error?.message === 'string') {
          message = responseData.error.message;
        } else if (typeof responseData.message === 'string') {
          message = responseData.message;
        } else if (Array.isArray(responseData.message)) {
          message = responseData.message.join(', ');
        } else if (typeof responseData.error === 'string') {
          message = responseData.error;
        }
      }
    }

    const lower = message.toLowerCase();

    // Friendly messages for known error strings
    if (
      lower.includes('invalid email or password') ||
      lower.includes('invalid credentials') ||
      lower.includes('invalid_credentials') ||
      lower.includes('unauthorized') ||
      lower.includes('user not found') ||
      lower.includes('usuário não encontrado') ||
      lower.includes('usuario nao encontrado')
    ) {
      return 'E-mail ou senha inválidos. Por favor, verifique suas credenciais.';
    }

    const isConflict =
      status === 409 ||
      lower.includes('conflict') ||
      lower.includes('conflit') ||
      lower.includes('already exist') ||
      lower.includes('já cadastrado') ||
      lower.includes('ja existe') ||
      lower.includes('duplicad');

    if (isConflict) {
      return 'Este e-mail corporativo já está cadastrado no sistema. Por favor, faça login ou recupere seu acesso.';
    }

    // Network / Offline / Connection errors
    if (!error.response || error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      return 'Não foi possível conectar ao servidor. Verifique sua conexão de internet ou se o serviço está temporariamente indisponível.';
    }

    if (status === 401) {
      if (url.includes('/auth/login')) {
        return 'E-mail ou senha inválidos. Por favor, verifique suas credenciais.';
      }
      return 'Sua sessão expirou ou não está autorizada. Faça login novamente.';
    }

    if (status === 403) {
      return 'Você não possui permissão para realizar esta ação.';
    }

    if (status === 404) {
      if (message) return message;
      if (url.includes('auth/login')) {
        return 'E-mail ou senha inválidos. Por favor, verifique suas credenciais.';
      }
      return 'Recurso ou rota não encontrada no servidor.';
    }

    if (status && status >= 500) {
      return 'Erro interno no servidor. Por favor, tente novamente em alguns instantes.';
    }

    if (message && status && status < 500) {
      return message;
    }

    if (status && status >= 500) {
      return 'Erro interno no servidor. Por favor, tente novamente em alguns instantes.';
    }

    if (message) {
      return message;
    }
  }

  if (error instanceof Error) {
    let msg = error.message;
    if (msg.startsWith('[Cyzor API Error]')) {
      const parts = msg.split(' - ');
      if (parts.length >= 3) {
        msg = parts.slice(2).join(' - ');
      }
    }

    const lower = msg.toLowerCase();
    if (
      lower.includes('network error') ||
      lower.includes('err_network') ||
      lower.includes('status: n/a')
    ) {
      return 'Não foi possível conectar ao servidor. Verifique sua conexão de internet ou se o serviço está temporariamente indisponível.';
    }
    if (
      lower.includes('invalid email or password') ||
      lower.includes('invalid credentials') ||
      lower.includes('unauthorized') ||
      lower.includes('user not found')
    ) {
      return 'E-mail ou senha inválidos. Por favor, verifique suas credenciais.';
    }
    if (
      lower.includes('conflict') ||
      lower.includes('conflit') ||
      lower.includes('already exist') ||
      lower.includes('já cadastrado') ||
      lower.includes('ja existe')
    ) {
      return 'Este e-mail corporativo já está cadastrado no sistema. Por favor, faça login ou recupere seu acesso.';
    }
    return msg;
  }

  return 'Ocorreu um erro inesperado. Por favor, tente novamente.';
}
